import { useInfiniteQuery, useQuery, hashQueryKey } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useWorkspace } from '@opallabs/hooks'
import {
  apiV3,
  globalQueryKeys,
  OpalApiComponent,
  OpalApiResponseDocument,
} from '../../../../index'
import { findResourceOfType } from '../../../schema/validation/resourceObject'

type UsersResponse = OpalApiResponseDocument<'ReadUsersV3', 200>
type UserGroupsResponse = OpalApiResponseDocument<'ReadUserGroupsV3', 200>
type UsersAndGroupsRawData = [UsersResponse, UserGroupsResponse]

type UserOrGroupSelectOption = {
  email?: string
  id: string
  label: string
  avatarUrl: string
  isActive: boolean
  resource_type: 'user' | 'user_group'
}

type UsersAndGroupsTransformedData = UserOrGroupSelectOption[]
type UseUsersAndGroupsOptions<T = UsersAndGroupsTransformedData> = {
  enabled?: boolean
  select?: (data: UsersAndGroupsRawData) => T
}

const transformUser = (
  user: OpalApiComponent<'V3', 'user'>,
  included: NonNullable<UsersResponse['included']>
): UserOrGroupSelectOption => {
  const profile = included?.find(
    findResourceOfType(
      'user_profile',
      'V3',
      user.relationships.profile.data?.id
    )
  )
  const asset = profile
    ? included?.find(
        findResourceOfType(
          'asset_reference',
          'V3',
          profile.relationships.avatar_asset_reference.data?.id
        )
      )
    : undefined

  return {
    id: user.id,
    email: user.attributes.email,
    label: user.attributes.is_active
      ? user.attributes.full_name
      : `${user.attributes.full_name} (Deactivated)`,
    avatarUrl: asset?.attributes.preview_url ?? '',
    isActive: user.attributes.is_active ?? false,
    resource_type: 'user',
  }
}

const transformUserGroup = (
  group: OpalApiComponent<'V3', 'user_group'>,
  included: NonNullable<UserGroupsResponse['included']>
): UserOrGroupSelectOption => {
  const asset = included.find(
    findResourceOfType('asset', 'V3', group.relationships.asset?.data?.id)
  )

  return {
    id: group.id,
    label: group.attributes.name,
    avatarUrl: asset?.attributes.url ?? '',
    isActive: true,
    resource_type: 'user_group',
  }
}

export const usersAndGroupsSelect = (
  data: UsersAndGroupsRawData
): UsersAndGroupsTransformedData => {
  const [usersResponse, userGroupsResponse] = data
  const users = usersResponse.data.map((user) =>
    transformUser(user, usersResponse.included || [])
  )
  const userGroups = (userGroupsResponse.data || []).map((group) =>
    transformUserGroup(group, userGroupsResponse.included || [])
  )
  return [...users, ...userGroups]
}

const USERS_PAGE_LIMIT = 100

/**
 * If multiple instances of this query are rerendered at the same time,
 * we can hit a race condition where the same API request is fired off
 * several times. We'll use this Set to store the hashed query keys that
 * that are actively fetching a page to prevent those duplicates.
 */
const activeQuerySet = new Set()

export const useUsersAndGroups = <T = UsersAndGroupsTransformedData>({
  enabled = true,
  select = usersAndGroupsSelect as (data: UsersAndGroupsRawData) => T,
}: UseUsersAndGroupsOptions<T> = {}) => {
  const { uuid: workspaceUuid } = useWorkspace()

  const usersQueryKey = globalQueryKeys.workspaceUsers(workspaceUuid)
  const hashedUsersQueryKey = hashQueryKey(usersQueryKey)

  const {
    data: usersPages,
    isFetching: isUsersFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    enabled,
    refetchInterval: false,
    // Query key is set up above so that we can also hash it
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: usersQueryKey,
    queryFn: async ({ pageParam }) => {
      return await apiV3.users.readCollection({
        query: {
          include: ['profile', 'profile.avatar_asset_reference'],
          filter: { workspace_id: [workspaceUuid] },
          page: {
            offset: (pageParam ?? 0) * USERS_PAGE_LIMIT,
            limit: USERS_PAGE_LIMIT,
          },
        },
      })
    },
    getNextPageParam: (_lastPage, allPages) => {
      const offset = allPages.length * USERS_PAGE_LIMIT
      if (!allPages[0].meta) return false
      if (allPages[0].meta.total > offset) {
        return allPages.length
      }
      return undefined
    },
  })

  useEffect(() => {
    if (!usersPages || !hasNextPage) return
    if (isUsersFetching || isFetchingNextPage) return

    // This query is already running a fetch in another query instance,
    // but the isFetchingNextPage flag may not yet have rerendered
    if (activeQuerySet.has(hashedUsersQueryKey)) return

    // If we're ready to fetch the next page, register this query instance's key
    // to the set to help prevent duplicate queries from race conditions
    activeQuerySet.add(hashedUsersQueryKey)
    fetchNextPage().finally(() => {
      activeQuerySet.delete(hashedUsersQueryKey)
    })
  }, [
    isFetchingNextPage,
    isUsersFetching,
    usersPages,
    fetchNextPage,
    hasNextPage,
    hashedUsersQueryKey,
  ])

  const { data: groupsResponse, isLoading: isGroupsLoading } = useQuery({
    enabled,
    queryKey: globalQueryKeys.groups(
      { members: { workspace_id: [workspaceUuid] } },
      ['asset']
    ),
    queryFn: () =>
      apiV3.userGroups.readCollection({
        params: {
          query: {
            include: ['asset'],
            filter: { members: { workspace_id: [workspaceUuid] } },
          },
        },
      }),
  })

  const fetchingComplete =
    !enabled || (!hasNextPage && !isUsersFetching && !isGroupsLoading)

  const data = useMemo(() => {
    if (!usersPages || !fetchingComplete || !groupsResponse) return undefined

    const usersResponse: UsersResponse = {
      data: usersPages.pages.flatMap((page) => page.data),
      included: usersPages.pages.flatMap((page) => page.included || []),
      meta: usersPages.pages[0].meta,
    }

    return select([usersResponse, groupsResponse])
  }, [usersPages, fetchingComplete, groupsResponse, select])

  return useMemo(
    () => ({ data, isLoading: !fetchingComplete, fetchingComplete }),
    [data, fetchingComplete]
  )
}
